/**
 * WeeklyOrdersPopover Component
 *
 * Lightweight popover for quick one-click confirmation of recurring orders.
 * Groups active recurring orders by day of week (Mon-Sun) and shows a "+"
 * button to instantly create a pending sale for the next occurrence of that day.
 *
 * The full management modal (edit, pause, create) is accessible via the
 * "Manage Orders" link in the footer.
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { format, addDays, getISODay } from 'date-fns'
import { CalendarClock, RefreshCcw, Plus, Check, Loader2, ArrowRight } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { toast } from 'sonner'
import {
  fetchRecurringOrders,
  fetchInstancesForDateRange,
  createSingleInstance,
  confirmInstance
} from '@/services/recurringOrderService'

// Day configuration (ISO weekday: 1=Mon, 7=Sun)
const DAYS_OF_WEEK = [
  { number: 1, name: 'Monday', short: 'Mon' },
  { number: 2, name: 'Tuesday', short: 'Tue' },
  { number: 3, name: 'Wednesday', short: 'Wed' },
  { number: 4, name: 'Thursday', short: 'Thu' },
  { number: 5, name: 'Friday', short: 'Fri' },
  { number: 6, name: 'Saturday', short: 'Sat' },
  { number: 7, name: 'Sunday', short: 'Sun' }
]

function formatItemsList(items) {
  if (!items || items.length === 0) return []
  return items.map(item => `${item.product?.strain || 'Unknown'} ${item.quantity}kg`)
}

/**
 * Calculate the next occurrence of a given ISO day number within the current week.
 * Returns today if the day matches, otherwise the matching day this week.
 */
function getNextOccurrence(isoDayNumber) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const todayIso = getISODay(today)
  if (isoDayNumber === todayIso) return today
  const daysAhead = (isoDayNumber - todayIso + 7) % 7
  return addDays(today, daysAhead)
}

export default function WeeklyOrdersPopover({ onOrderConfirmed, onManageOrders }) {
  const [open, setOpen] = useState(false)
  const [orders, setOrders] = useState([])
  const [instances, setInstances] = useState([])
  const [loading, setLoading] = useState(false)
  const [confirmingKeys, setConfirmingKeys] = useState(new Set())
  const [confirmedKeys, setConfirmedKeys] = useState(new Set())

  // Rolling 7-day window from today — matches the dates getNextOccurrence can return
  // (each day-of-week resolves to "today" or the next 6 days going forward).
  const weekRange = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return {
      from: format(today, 'yyyy-MM-dd'),
      to: format(addDays(today, 6), 'yyyy-MM-dd')
    }
  }, [])

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [ordersData, instancesData] = await Promise.all([
        fetchRecurringOrders({ activeOnly: true }),
        fetchInstancesForDateRange(weekRange.from, weekRange.to)
      ])
      setOrders(ordersData)
      setInstances(instancesData)
    } catch (err) {
      console.error('[WeeklyOrdersPopover] Error loading data:', err)
      toast.error('Failed to load weekly orders')
    } finally {
      setLoading(false)
    }
  }, [weekRange])

  // Fetch data when popover opens
  useEffect(() => {
    if (!open) return
    setConfirmedKeys(new Set())
    loadData()
  }, [open, loadData])

  // Filter out paused orders and group by day
  const ordersByDay = useMemo(() => {
    const today = format(new Date(), 'yyyy-MM-dd')
    const activeOrders = orders.filter(order => {
      if (order.paused_until && order.paused_until >= today) return false
      return true
    })

    const grouped = {}
    DAYS_OF_WEEK.forEach(day => { grouped[day.number] = [] })

    activeOrders.forEach(order => {
      const days = order.days_of_week || []
      days.forEach(dayNum => {
        if (grouped[dayNum]) grouped[dayNum].push(order)
      })
    })

    // Sort each day's orders alphabetically by customer name
    Object.keys(grouped).forEach(dayNum => {
      grouped[dayNum].sort((a, b) => {
        const nameA = a.customer?.name || ''
        const nameB = b.customer?.name || ''
        return nameA.localeCompare(nameB)
      })
    })

    return grouped
  }, [orders])

  // Single lookup of every loaded instance, keyed by `${recurringOrderId}-${scheduledDate}`.
  // Using one map lets handleConfirm distinguish pending / confirmed / skipped / cancelled
  // states cleanly and avoids INSERTing a duplicate when a pre-generated instance exists.
  const instanceByKey = useMemo(() => {
    const map = new Map()
    instances.forEach(inst => {
      map.set(`${inst.recurring_order_id}-${inst.scheduled_date}`, inst)
    })
    return map
  }, [instances])

  const isConfirmedInstance = (inst) => inst.status === 'confirmed' || !!inst.sale_id

  const handleConfirm = useCallback(async (order, dayNumber) => {
    const key = `${order.id}-${dayNumber}`
    if (confirmingKeys.has(key) || confirmedKeys.has(key)) return

    setConfirmingKeys(prev => new Set(prev).add(key))

    try {
      const targetDate = getNextOccurrence(dayNumber)
      const targetDateStr = format(targetDate, 'yyyy-MM-dd')
      const instanceKey = `${order.id}-${targetDateStr}`

      // Use cached instance if known; otherwise ask the service (it will return
      // any pre-existing row if our cache was stale, so we never blind-INSERT).
      const instance = instanceByKey.get(instanceKey)
        ?? await createSingleInstance(order.id, targetDateStr)

      if (isConfirmedInstance(instance)) {
        setConfirmedKeys(prev => new Set(prev).add(key))
        toast.info('Already confirmed for this week')
        return
      }
      if (instance.status !== 'pending') {
        toast.error(`Cannot confirm — instance is ${instance.status}`, {
          description: 'Use Manage Orders to change its status.'
        })
        return
      }

      // Confirm the instance (creates sale with current prices)
      const sale = await confirmInstance(instance.id)

      setConfirmedKeys(prev => new Set(prev).add(key))
      toast.success('Order confirmed', {
        description: `${order.customer?.name} — ${format(targetDate, 'EEE d MMM')}`
      })

      onOrderConfirmed?.(sale.id)
    } catch (err) {
      console.error('[WeeklyOrdersPopover] Error confirming order:', err)
      toast.error('Failed to confirm order', { description: err.message })
    } finally {
      setConfirmingKeys(prev => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }, [confirmingKeys, confirmedKeys, instanceByKey, onOrderConfirmed])

  const getRowState = useCallback((order, dayNumber) => {
    const key = `${order.id}-${dayNumber}`
    if (confirmingKeys.has(key)) return 'confirming'
    if (confirmedKeys.has(key)) return 'confirmed'

    const targetDate = getNextOccurrence(dayNumber)
    const targetDateStr = format(targetDate, 'yyyy-MM-dd')
    const existing = instanceByKey.get(`${order.id}-${targetDateStr}`)
    if (existing && isConfirmedInstance(existing)) return 'confirmed'

    return 'default'
  }, [confirmingKeys, confirmedKeys, instanceByKey])

  // Check if there are any orders at all
  const hasOrders = useMemo(() => {
    return DAYS_OF_WEEK.some(day => (ordersByDay[day.number] || []).length > 0)
  }, [ordersByDay])

  // Only show days that have orders
  const daysWithOrders = useMemo(() => {
    return DAYS_OF_WEEK.filter(day => (ordersByDay[day.number] || []).length > 0)
  }, [ordersByDay])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline">
          <CalendarClock className="w-4 h-4 mr-2" />
          Weekly Orders
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[520px] p-0 bg-white flex flex-col"
        style={{ maxHeight: '70vh', overflow: 'hidden' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-sm">Weekly Orders</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="h-7 w-7 p-0"
          >
            <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Content */}
        <ScrollArea className="flex-1 min-h-0 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : !hasOrders ? (
            <div className="py-8 px-4 text-center">
              <CalendarClock className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No recurring orders</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Create one from the Manage Orders screen
              </p>
            </div>
          ) : (
            <div>
              {daysWithOrders.map((day) => (
                <div key={day.number}>
                  {/* Day header */}
                  <div className="sticky top-0 z-10 bg-muted/50 border-b px-4 py-1.5">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {day.name}
                    </span>
                  </div>

                  {/* Order rows */}
                  {ordersByDay[day.number].map((order) => {
                    const state = getRowState(order, day.number)
                    const items = formatItemsList(order.items)
                    const customerName = order.customer?.name || 'Unknown'
                    const branchName = order.branch?.name && order.branch.name.toLowerCase() !== 'main'
                      ? order.branch.name
                      : null

                    return (
                      <div
                        key={`${order.id}-${day.number}`}
                        className="flex items-center gap-3 pl-8 pr-4 py-2 border-b border-border/30 hover:bg-muted/20 transition-colors"
                      >
                        {/* Customer name + branch */}
                        <div className="min-w-0 flex-shrink-0 w-[140px]">
                          <span className="text-sm font-medium truncate block">
                            {customerName}{branchName ? ` ${branchName}` : ''}
                          </span>
                        </div>

                        {/* Items — one per line */}
                        <div className="flex-1 min-w-0">
                          {items.length > 0 ? items.map((item, idx) => (
                            <span key={idx} className="text-xs text-muted-foreground block truncate">
                              {item}
                            </span>
                          )) : (
                            <span className="text-xs text-muted-foreground italic">No items</span>
                          )}
                        </div>

                        {/* Action button */}
                        {state === 'confirming' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled
                            className="h-7 w-7 p-0 flex-shrink-0"
                          >
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          </Button>
                        ) : state === 'confirmed' ? (
                          <div className="h-7 w-7 flex items-center justify-center flex-shrink-0">
                            <Check className="h-4 w-4 text-green-600" />
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleConfirm(order, day.number)}
                            className="h-7 w-7 p-0 flex-shrink-0 border-green-300 text-green-700 hover:bg-green-50 hover:border-green-400"
                            title={`Confirm ${order.customer?.name} for ${day.name}`}
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="border-t px-4 py-2.5">
          <button
            onClick={() => {
              setOpen(false)
              onManageOrders?.()
            }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            Manage Orders
            <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
